// app/javascript/components/LinkCard.jsx

import React, { useState, useEffect, useCallback, useRef } from 'react';

// 画像読み込み状態を管理するカスタムフック
const useImageLoader = (src) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const isMounted = useRef(true); // コンポーネントがマウントされているか追跡

  // マウント解除時にフラグを更新
  useEffect(() => {
      isMounted.current = true;
      return () => {
          isMounted.current = false;
      };
  }, []);

  // srcが変更されたら状態をリセット
  useEffect(() => {
    console.log(`useImageLoader: src changed to: ${src}`);
    if (src) {
      // 新しいsrcがセットされたら、常にローディング開始、エラーリセット
      setIsLoading(true);
      setHasError(false);
      console.log(`useImageLoader: src loaded to: ${src}`)
    } else {
      // src が null or undefined の場合 (画像がない場合)
      setIsLoading(false); // ローディングではない
      setHasError(false); // エラーでもない
      console.log(`useImageLoader: src null`)
    }
  }, [src]);

  const handleLoad = useCallback(() => {
    // console.log(`useImageLoader: onLoad triggered for ${src}`);
    // アンマウント後に状態更新しないようにチェック
    if (isMounted.current) {
        setIsLoading(false);
        setHasError(false);
    }
  }, [src]); // srcに依存させることで、srcが変わったら新しいハンドラが作られる

  const handleError = useCallback(() => {
    // console.log(`useImageLoader: onError triggered for ${src}`);
    // アンマウント後に状態更新しないようにチェック
    if (isMounted.current) {
        setIsLoading(false);
        setHasError(true);
    }
  }, [src]); // srcに依存させる

  return {
    isLoading: isLoading && !!src, // srcが存在し、かつローディングが完了していない状態
    hasError: hasError,
    imgProps: { // imgタグに渡すためのプロパティ群
      src: src,
      onLoad: handleLoad,
      onError: handleError,
      // key: src, // 強制再レンダリングが必要な場合のオプション
      hidden: !src, // srcがない場合はimg要素を非表示にする (レイアウトスペースは確保される可能性あり)
      // loading="lazy" // ここに含めるか、imgタグで直接指定するか
    }
  };
};

// 画像がない場合やエラー時に表示するプレースホルダーアイコン
const PlaceholderIcon = () => (
  <svg className="w-10 h-10 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>
);

// --- LinkCard コンポーネント本体 ---
const LinkCard = ({ url }) => {
  const [metadata, setMetadata] = useState(null);
  const [isLoadingMetadata, setIsLoadingMetadata] = useState(true); // メタデータ取得自体のローディング状態
  const [fetchError, setFetchError] = useState(null);

  // メタデータ取得のための useEffect
  useEffect(() => {
    // urlが空、または無効な形式の場合は処理しない
    if (!url || !url.match(/^https?:\/\//)) {
      console.log("loading")
      setIsLoadingMetadata(false); // ローディング完了
      setFetchError('有効なURLを指定してください。'); // エラーメッセージ設定
      setMetadata(null); // メタデータはnull
      return;
    }

    const controller = new AbortController(); // AbortControllerで中断可能にする
    const signal = controller.signal;

    const fetchMetadata = async () => {
      setIsLoadingMetadata(true); // メタデータ取得開始
      setFetchError(null);      // エラー状態をリセット
      setMetadata(null);        // 既存のメタデータをクリア

      try {
        const response = await fetch(`/api/v1/link_preview?url=${encodeURIComponent(url)}`, { signal });

        if (!response.ok) {
          // エラーレスポンスからJSONをパース試行、失敗したらステータスコードでエラー作成
          const errorData = await response.json().catch(() => ({ error: `HTTPエラー: ${response.status} ${response.statusText}` }));
          throw new Error(errorData.error || `HTTPエラー: ${response.status}`);
        }

        const data = await response.json();
        if (signal.aborted) return; // fetchが中断されていたらステートを更新しない
        setMetadata(data);

      } catch (err) {
        if (err.name === 'AbortError') {
          console.log('Fetch aborted'); // 中断された場合はコンソールにログ表示
        } else {
          console.error("Error fetching link preview:", err);
          if (!signal.aborted) { // 中断されていなければエラーステートを更新
             setFetchError(err.message || 'リンク情報の取得中に不明なエラーが発生しました。');
          }
        }
      } finally {
         if (!signal.aborted) { // 中断されていなければローディング完了
           setIsLoadingMetadata(false);
         }
      }
    };

    fetchMetadata();

    // クリーンアップ関数: コンポーネントがアンマウントされるか、urlが変わる前に実行
    return () => {
      controller.abort(); // 進行中のfetchリクエストを中断
    };
  }, [url]); // urlが変更されたらこのeffectを再実行

  // useImageLoaderフックを使用して画像の状態を管理
  const mainImage = useImageLoader(metadata?.image); // メタデータ取得後にimage URLがセットされる
  const favicon = useImageLoader(metadata?.favicon); // 同様にfavicon URLがセットされる

  // --- ローディング中の表示 (メタデータ取得中) ---
  if (isLoadingMetadata) {
    // スケルトン表示（Tailwindのanimate-pulseを使用）
    return (
      <div className="block w-full max-w-md mx-auto my-4 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden bg-white dark:bg-gray-800 animate-pulse">
        <div className="flex flex-col sm:flex-row">
          {/* Image Placeholder */}
          <div className="flex-shrink-0 sm:w-32 h-32 sm:h-auto bg-gray-300 dark:bg-gray-600"></div>
          {/* Text Placeholder */}
          <div className="p-4 flex-1">
            <div className="h-5 bg-gray-300 dark:bg-gray-600 rounded w-3/4 mb-2"></div>
            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-full mb-1"></div>
            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-5/6 mb-3"></div>
            <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded w-1/4"></div>
          </div>
        </div>
      </div>
    );
  }

  // --- エラー発生時の表示 (メタデータ取得失敗) ---
  if (fetchError) {
    return (
      <div className="w-full max-w-md mx-auto my-4 border border-red-200 dark:border-red-700 rounded-lg p-4 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300">
        <p className="font-medium text-sm">リンクプレビューを取得できませんでした。</p>
        <p className="text-xs mt-1">{fetchError}</p>
        {/* エラー時も元のURLへのリンクは表示 */}
        <a href={url} target="_blank" rel="noopener noreferrer" className="block text-xs text-blue-600 dark:text-blue-400 hover:underline mt-2 break-all">
          {url}
        </a>
      </div>
    );
  }

  // --- メタデータ取得成功時の表示 ---
  // メタデータがnull（正常終了したがデータがない場合など）は何も表示しない
  if (!metadata) {
    return null;
  }

  // --- メインのリンクカード表示 ---
  return (
    <a
      href={metadata.url}
      target="_blank"
      rel="noopener noreferrer nofollow" // rel属性を設定
      className="block w-full max-w-md mx-auto my-4 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-md transition-all duration-200 ease-in-out no-underline bg-white dark:bg-gray-800 group" // groupクラスでホバー効果の起点に
    >
      <div className="flex flex-col sm:flex-row"> {/* スマートフォンでは縦積み、sm以上で横並び */}
        {/* --- Image Section --- */}
        <div className="flex-shrink-0 sm:w-32 h-32 sm:h-auto bg-gray-100 dark:bg-gray-700 flex items-center justify-center overflow-hidden"> {/* 画像コンテナ */}
          {/* 画像ローディング中 */}
          {mainImage.isLoading && (
             <div className="w-full h-full bg-gray-300 dark:bg-gray-600 animate-pulse"></div>
          )}
          {/* 画像読み込み成功 */}
          {/* isLoading: false, hasError: false, srcが存在する場合にimgタグを表示 */}
          {!mainImage.hasError && mainImage.imgProps.src && (
            <img
              alt={metadata.title || 'Link preview image'} // 代替テキスト
              className="object-cover w-full h-full" // 画像表示スタイル
              {...mainImage.imgProps} // src, onLoad, onError, hidden を展開
              loading="lazy" // ネイティブ遅延読み込み
            />
          )}
          {/* 画像読み込みエラーまたは画像srcがない場合 (isLoading: falseの時) */}
          {!mainImage.isLoading && (mainImage.hasError || !mainImage.imgProps.src) && (
            <PlaceholderIcon /> // 代替アイコン表示
          )}
        </div>

        {/* --- Text Content Section --- */}
        <div className="p-4 flex flex-col justify-between leading-normal flex-grow min-w-0"> {/* テキスト部分 */}
          <div> {/* 上部コンテンツ（タイトル、説明） */}
            {/* タイトル */}
            <h3 className="text-gray-900 dark:text-gray-100 font-semibold text-base sm:text-lg mb-1 line-clamp-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-150"> {/* タイトルスタイル */}
              {metadata.title || 'タイトル不明'}
            </h3>
            {/* 説明 (存在する場合のみ表示) */}
            {metadata.description && (
              <p className="text-gray-700 dark:text-gray-300 text-sm mb-2 line-clamp-2 sm:line-clamp-3"> {/* 説明スタイル */}
                {metadata.description}
              </p>
            )}
          </div>
          {/* サイト情報 (ファビコン + サイト名) */}
          <div className="flex items-center mt-auto pt-1"> {/* 下部コンテンツ（サイト情報） */}
            {/* ファビコン (ローディング完了、エラーなし、srcありの場合) */}
            {!favicon.hasError && favicon.imgProps.src && (
              <img
                alt="" // 装飾的なのでaltは空
                className="w-4 h-4 mr-1.5 flex-shrink-0" // ファビコンスタイル
                {...favicon.imgProps} // src, onLoad, onError, hidden を展開
                loading="lazy" // ネイティブ遅延読み込み
              />
            )}
            {/* サイト名 */}
            <span className="text-gray-600 dark:text-gray-400 text-xs truncate"> {/* サイト名スタイル */}
              {/* metadata.site_nameがあればそれを表示、なければURLからホスト名を表示 */}
              {metadata.site_name || (metadata?.url ? new URL(metadata.url).hostname : '')}
            </span>
          </div>
        </div>
      </div>
    </a>
  );
};

export default LinkCard;
